import { supervisorMonitoringRepository } from '../repositories/SupervisorMonitoringRepository';
import type {
  SupervisorSession,
  SupervisorAction,
  ActiveCallMonitoring,
  CreateSupervisorSessionRequest,
  UpdateSupervisorSessionRequest,
  CreateSupervisorActionRequest,
  SupervisorActiveSession,
  SupervisionType,
} from '../types/supervisor-monitoring';

export class SupervisorMonitoringService {
  // ============================================================================
  // SUPERVISOR SESSIONS
  // ============================================================================

  async getActiveSessions(companyId: string): Promise<SupervisorSession[]> {
    return supervisorMonitoringRepository.getActiveSessions(companyId);
  }

  async getSessionById(id: string): Promise<SupervisorSession | null> {
    return supervisorMonitoringRepository.getSessionById(id);
  }

  async startMonitoring(
    companyId: string,
    supervisorId: string,
    callId: string,
    conferenceName: string,
    sessionType: SupervisionType = 'monitor'
  ): Promise<SupervisorSession> {
    const request: CreateSupervisorSessionRequest = {
      company_id: companyId,
      supervisor_id: supervisorId,
      agent_id: null, // Will be populated from call data
      call_id: callId,
      conference_name: conferenceName,
      session_type: sessionType,
    };

    const session = await supervisorMonitoringRepository.createSession(request);

    // Log the action
    await supervisorMonitoringRepository.logAction(
      session.id,
      'start_monitor',
      null,
      sessionType
    );

    return session;
  }

  async changeSessionType(
    sessionId: string,
    fromType: SupervisionType,
    toType: SupervisionType
  ): Promise<SupervisorSession> {
    const session = await supervisorMonitoringRepository.updateSession(sessionId, {
      session_type: toType,
    });

    // Log the action
    await supervisorMonitoringRepository.logAction(
      sessionId,
      this.getActionTypeFromTransition(fromType, toType),
      fromType,
      toType
    );

    return session;
  }

  async endSession(sessionId: string): Promise<SupervisorSession> {
    const session = await supervisorMonitoringRepository.endSession(sessionId);

    // Log the action
    await supervisorMonitoringRepository.logAction(
      sessionId,
      'end_session',
      session.session_type,
      null
    );

    return session;
  }

  async getSupervisorActiveSessions(
    supervisorId: string
  ): Promise<SupervisorActiveSession[]> {
    return supervisorMonitoringRepository.getSupervisorActiveSessions(supervisorId);
  }

  // ============================================================================
  // SUPERVISOR ACTIONS
  // ============================================================================

  async getSessionActions(sessionId: string): Promise<SupervisorAction[]> {
    return supervisorMonitoringRepository.getSessionActions(sessionId);
  }

  async logAction(
    sessionId: string,
    actionType: string,
    fromState?: string | null,
    toState?: string | null,
    notes?: string | null
  ): Promise<string> {
    return supervisorMonitoringRepository.logAction(
      sessionId,
      actionType,
      fromState,
      toState,
      notes
    );
  }

  // ============================================================================
  // ACTIVE CALLS MONITORING
  // ============================================================================

  async getActiveCallsForMonitoring(
    companyId: string
  ): Promise<ActiveCallMonitoring[]> {
    return supervisorMonitoringRepository.getActiveCallsForMonitoring(companyId);
  }

  async getCallDetails(callId: string): Promise<ActiveCallMonitoring | null> {
    return supervisorMonitoringRepository.getCallDetails(callId);
  }

  // ============================================================================
  // REALTIME SUBSCRIPTIONS
  // ============================================================================

  subscribeToActiveCalls(
    companyId: string,
    callback: (payload: any) => void
  ): () => void {
    return supervisorMonitoringRepository.subscribeToActiveCalls(companyId, callback);
  }

  subscribeToSupervisorSessions(
    companyId: string,
    callback: (payload: any) => void
  ): () => void {
    return supervisorMonitoringRepository.subscribeToSupervisorSessions(
      companyId,
      callback
    );
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  private getActionTypeFromTransition(
    fromType: SupervisionType,
    toType: SupervisionType
  ): string {
    if (toType === 'barge') return 'barge_in';
    if (toType === 'whisper') return 'whisper';
    if (toType === 'takeover') return 'takeover';
    if (toType === 'monitor') return 'start_monitor';
    return 'coach_agent';
  }

  /**
   * Validate if a supervisor can perform a specific action
   */
  canPerformAction(
    currentType: SupervisionType,
    targetType: SupervisionType
  ): boolean {
    // Define valid transitions
    const validTransitions: Record<SupervisionType, SupervisionType[]> = {
      monitor: ['barge', 'whisper', 'takeover'],
      barge: ['monitor', 'whisper', 'takeover'],
      whisper: ['monitor', 'barge', 'takeover'],
      takeover: ['monitor', 'barge', 'whisper'],
    };

    return validTransitions[currentType]?.includes(targetType) ?? false;
  }

  /**
   * Get human-readable description of supervision type
   */
  getSupervisionTypeDescription(type: SupervisionType): string {
    const descriptions: Record<SupervisionType, string> = {
      monitor: 'Silent monitoring - listen only',
      barge: 'Barge in - all parties hear you',
      whisper: 'Whisper mode - only agent hears you',
      takeover: 'Take over - agent is muted, you handle the call',
    };

    return descriptions[type] || type;
  }
}

export const supervisorMonitoringService = new SupervisorMonitoringService();
